import { type QuizSetData } from '@/api';

import { type AnswerPageBlockData, type QuestionPageBlockData } from '../type';

export const getQuestion = (quiz: QuizSetData) => {
  switch (quiz.type) {
    case 'multiple':
      return quiz.multipleData?.question as string;
    case 'ox':
      return quiz.oxData?.question as string;
    case 'subjective':
      return quiz.subjectiveData?.question as string;
    case 'essay':
      return quiz.essayData?.question as string;
    default:
      return quiz.subjectiveData?.question as string;
  }
};

export const getQuestionRichText = (quiz: QuizSetData) => {
  let richText: string | undefined;

  switch (quiz.type) {
    case 'multiple':
      richText = quiz.multipleData?.questionRichText as string;
      break;
    case 'ox':
      richText = quiz.oxData?.questionRichText as string;
      break;
    case 'subjective':
      richText = quiz.subjectiveData?.questionRichText as string;
      break;
    case 'essay':
      richText = quiz.essayData?.questionRichText as string;
      break;
    default:
      richText = quiz.subjectiveData?.questionRichText as string;
      break;
  }

  if (!richText || richText.trim() === '') {
    return undefined;
  }

  return richText;
};

export const getQuizType = (quiz: QuizSetData) => {
  return quiz.type as 'multiple' | 'ox' | 'subjective' | 'essay';
};

export const quizDataToQuestionPageBlock = (quizzes: QuizSetData[]) => {
  const blocks: QuestionPageBlockData[] = [];

  quizzes.forEach((quiz, index) => {
    const question = getQuestion(quiz);
    const questionRichText = getQuestionRichText(quiz);
    blocks.push({
      type: 'question',
      data: {
        question,
        questionRichText,
        questionImageUrl: quiz.imageUrl,
        questionNumber: index + 1,
        questionType: getQuizType(quiz),
      },
    });
    switch (quiz.type) {
      case 'multiple':
        quiz.multipleData?.choiceListV2?.forEach((choice, choiceIndex) => {
          blocks.push({
            type: 'multipleChoice',
            data: {
              choice,
              choiceNumber: choiceIndex + 1,
            },
          });
        });
        break;
      case 'ox':
        blocks.push({
          type: 'oxChoice',
          data: 'o',
        });
        blocks.push({
          type: 'oxChoice',
          data: 'x',
        });
        break;
      case 'subjective':
        blocks.push({
          type: 'subjective',
          data: {},
        });
        break;
      case 'essay':
        blocks.push({
          type: 'essay',
          data: {},
        });
        break;
    }
  });

  return blocks;
};

export const quizDataToAnswerPageBlock = (quizzes: QuizSetData[]) => {
  const blocks: AnswerPageBlockData[] = [];

  quizzes.forEach((quiz, index) => {
    const question = getQuestion(quiz);
    const questionRichText = getQuestionRichText(quiz);
    blocks.push({
      type: 'question',
      data: {
        question,
        questionRichText,
        questionNumber: index + 1,
        questionType: getQuizType(quiz),
      },
    });
    switch (quiz.type) {
      case 'multiple':
        blocks.push({
          type: 'multipleChoiceAnswer',
          data: {
            answerNumberList: quiz.multipleData?.answerList ?? [],
          },
        });
        break;
      case 'ox':
        blocks.push({
          type: 'oxChoiceAnswer',
          data: quiz.oxData?.answer as 'o' | 'x',
        });
        break;
      case 'subjective':
        blocks.push({
          type: 'subjectiveAnswer',
          data: {
            answerList: quiz.subjectiveData?.answerList ?? [],
          },
        });
        break;
      case 'essay':
        blocks.push({
          type: 'essayAnswer',
          data: {},
        });
        break;
    }
  });

  return blocks;
};
